import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../apiHelpers';
import { IconButton, buttonClass } from '../components/ui/Button';
import { IconClose } from '../components/ui/icons';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';

/**
 * Post Show Notes — the crew's debrief for one show, in a modal.
 *
 * A MODAL, not an Event-page tab, and that was a considered call: the five tabs
 * mirror EventForm's and feed the completion ring, while a debrief is written
 * after the show and is never part of "is this show ready". Putting it in that
 * set implied it counted toward completion. It opens from a header button
 * instead.
 *
 * Mirrors the paper form the producers already pass around. Two things worth
 * knowing before changing it:
 *
 * `can_edit` comes from the SERVER, because only the server knows whether you
 * were on this show's crew. Never infer it from a capability flag here: the write
 * rule is membership, and the frontend does not hold the crew list.
 *
 * Event name and drones flown are OVERRIDES. Leaving the name alone keeps it
 * following the event record, so renaming a show does not leave a stale title
 * frozen in the report. That is why the save sends the field only when it differs
 * from the event's own name.
 */

interface PostShowNotes {
  event_id: string;
  event_name: string;
  drones_flown: number | null;
  lead_name: string;
  event_notes: string;
  launch_site_notes: string;
  extra_information: string;
  travel_notes: string;
  event_date: string;
  end_date: string | null;
  planned_drones: number;
  exists: boolean;
  updated_at: string | null;
  can_edit: boolean;
}

/** The four long-form sections, in the order the form asks for them. */
const SECTIONS: Array<{ key: keyof PostShowNotes; label: string; hint?: string }> = [
  { key: 'event_notes', label: 'Event specific notes' },
  { key: 'launch_site_notes', label: 'Launch site and load in' },
  { key: 'extra_information', label: 'Extra information' },
  {
    key: 'travel_notes',
    label: 'Rental car, hotel, airport, box truck, shipping',
    hint: 'Anything the next crew should know. For example: the airport was too far and traffic was bad, try a different one.',
  },
];

const inputCls = `w-full m-0 bg-gray-900 day:bg-white border border-gray-700 day:border-gray-300 rounded
  px-2.5 py-1.5 text-sm text-on-surface day:text-gray-900 placeholder:text-on-surface-variant
  focus:border-primary/60 day:focus:border-amber-500 focus:outline-none transition-colors`;

interface Props {
  eventId: string;
  eventName: string;
  onClose: () => void;
}

const PostShowNotesModal: React.FC<Props> = ({ eventId, eventName, onClose }) => {
  const api = useApi();
  const [notes, setNotes] = useState<PostShowNotes | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const data: PostShowNotes = await api.get(`/events/${eventId}/post-show-notes`);
      setNotes(data);
    } catch (err: any) {
      setError(err?.message || 'Could not load the post show notes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  // Escape closes, same as EventAirspaceModal. Guarded while saving so a stray
  // keypress cannot discard a half-typed debrief mid-request.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const startEditing = () => {
    if (!notes) return;
    setForm({
      event_name: notes.event_name || '',
      drones_flown: notes.drones_flown === null ? '' : String(notes.drones_flown),
      lead_name: notes.lead_name || '',
      event_notes: notes.event_notes || '',
      launch_site_notes: notes.launch_site_notes || '',
      extra_information: notes.extra_information || '',
      travel_notes: notes.travel_notes || '',
    });
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        // The version we loaded. The server 409s if the row moved under us, which
        // is the difference between "your save failed" and "you erased their work".
        expected_updated_at: notes?.updated_at ?? null,
        // Send the name only when it differs from the event's, so an untouched
        // field keeps following the event rather than freezing a copy.
        event_name: form.event_name && form.event_name !== eventName ? form.event_name : '',
      };
      // api.put takes the payload inside its options object, not positionally.
      const data: PostShowNotes = await api.put(`/events/${eventId}/post-show-notes`, { body });
      setNotes(data);
      setEditing(false);
    } catch (err: any) {
      // A 409 means someone else saved while this form was open. Say so plainly and
      // leave their text alone in the boxes; Reload pulls the other version in.
      setError(err?.message || 'Could not save. Your text is still here, try again.');
    } finally {
      setSaving(false);
    }
  };

  const filled = notes ? SECTIONS.filter(s => (notes[s.key] as string)?.trim()).length : 0;

  /*
   * ONE return, with the states as branches inside it. The obvious alternative —
   * early-returning a <Shell> wrapper per state — defines a component during
   * render, so React remounts its subtree every keystroke and the textarea you
   * are typing in loses focus. Do not refactor this into that shape.
   */
  const content = (
    <div
      className="scrim z-[1000] flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={() => { if (!saving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Post show notes"
    >
      <div
        className="w-full max-w-2xl my-auto bg-gray-850 day:bg-white border border-gray-700/60 day:border-gray-200 rounded-lg p-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
      {loading && (
        <div className="text-sm text-on-surface-variant day:text-gray-600 py-8 text-center">Loading post show notes...</div>
      )}

      {!loading && !notes && (
        <div className="text-sm text-red-400 day:text-red-700 py-8 text-center">
          {error || 'No post show notes available.'}
        </div>
      )}

      {!loading && notes && (<FadeIn>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-on-surface day:text-gray-900">Post Show Notes</h3>
          <p className="text-xs text-on-surface-variant day:text-gray-600 mt-0.5">
            {notes.exists
              ? `${filled} of ${SECTIONS.length} sections filled in.`
              : 'What went well, what to change, what the next crew should know.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {notes.can_edit && !editing && (
            <button
              onClick={startEditing}
              className={buttonClass('quiet', 'sm')}
            >
              {notes.exists ? 'Edit notes' : 'Add notes'}
            </button>
          )}
          <button
            onClick={onClose}
            disabled={saving}
            aria-label="Close post show notes"
            className="p-1.5 rounded text-on-surface-variant day:text-gray-500 hover:text-on-surface day:hover:text-gray-900
              hover:bg-gray-700/50 day:hover:bg-gray-100 disabled:opacity-40 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <Reveal show={!!error}>
        <div className="mb-3 text-xs text-red-400 day:text-red-700 bg-red-500/10 border border-red-500/30 rounded px-2.5 py-2">
          {error}
        </div>
      </Reveal>

      {!editing && !notes.exists && (
        <p className="text-sm text-on-surface-variant day:text-gray-600 py-6 text-center">
          {notes.can_edit
            ? 'Nothing written yet. Add what you learned while it is fresh.'
            : 'Nothing written yet. The crew on this show can add it.'}
        </p>
      )}

      {/* ── Read view ── */}
      {!editing && notes.exists && (
        <FadeIn key="read" className="space-y-4">
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600">Show</dt>
              <dd className="text-on-surface day:text-gray-900 truncate" title={notes.event_name}>{notes.event_name}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600">Date</dt>
              <dd className="text-on-surface day:text-gray-900">{notes.event_date}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600">Drones flown</dt>
              <dd className="text-on-surface day:text-gray-900">
                {notes.drones_flown === null ? '—' : notes.drones_flown}
                <span className="text-on-surface-variant day:text-gray-400 text-xs ml-1">of {notes.planned_drones} planned</span>
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600">Lead</dt>
              <dd className="text-on-surface day:text-gray-900 truncate">{notes.lead_name || '—'}</dd>
            </div>
          </dl>

          {SECTIONS.map(({ key, label }) => {
            const value = (notes[key] as string) || '';
            if (!value.trim()) return null;
            return (
              <div key={key as string}>
                <div className="text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600 mb-1">{label}</div>
                {/* whitespace-pre-wrap: the crew type paragraphs and line breaks,
                    and losing them makes a debrief unreadable. */}
                <p className="text-sm text-on-surface day:text-gray-800 whitespace-pre-wrap">{value}</p>
              </div>
            );
          })}
        </FadeIn>
      )}

      {/* ── Edit view ── */}
      {editing && (
        <FadeIn key="edit" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600 mb-1">Show name</span>
              <input
                className={inputCls}
                value={form.event_name}
                placeholder={eventName}
                onChange={e => setForm(f => ({ ...f, event_name: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600 mb-1">
                Drones flown
              </span>
              <input
                className={inputCls}
                type="number"
                min={0}
                value={form.drones_flown}
                placeholder={`${notes.planned_drones} planned`}
                onChange={e => setForm(f => ({ ...f, drones_flown: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600 mb-1">Lead</span>
              <input
                className={inputCls}
                value={form.lead_name}
                placeholder="Who ran the show"
                onChange={e => setForm(f => ({ ...f, lead_name: e.target.value }))}
              />
            </label>
          </div>

          {SECTIONS.map(({ key, label, hint }) => (
            <label className="block" key={key as string}>
              <span className="block text-[11px] uppercase tracking-wider text-on-surface-variant day:text-gray-600 mb-1">{label}</span>
              {hint && <span className="block text-xs text-on-surface-variant day:text-gray-500 mb-1.5">{hint}</span>}
              <textarea
                className={`${inputCls} min-h-[90px] resize-y`}
                value={form[key as string]}
                onChange={e => setForm(f => ({ ...f, [key as string]: e.target.value }))}
              />
            </label>
          ))}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={() => { setEditing(false); setError(null); }}
              disabled={saving}
              className={buttonClass('quiet', 'md')}
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className={buttonClass('filled', 'md')}
            >
              {saving ? 'Saving...' : 'Save notes'}
            </button>
          </div>
        </FadeIn>
      )}
      </FadeIn>)}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default PostShowNotesModal;
